import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'
import { minorUnits } from '@/lib/money'

import { useHandoverBalances, useReceiveHandover } from '../api'
import type { WaiterBalance } from '../types'
import { useErrorText } from './OrderCard'

/**
 * Recevoir les espèces d'un serveur dans ce tiroir.
 *
 * Le serveur ne tient pas de caisse : ce qu'il encaisse à table est compté à
 * son nom. Il vient le vider ici ; **la caisse** compte les billets et saisit
 * la remise — c'est elle qui a l'argent en main et qui le recomptera à la
 * fermeture. Le montant proposé est ce qu'il doit, calculé par le serveur.
 */
export function HandoverPanel({
  eventId,
  money,
}: {
  eventId: string | undefined
  money: (minor: number) => string
}) {
  const { t } = useTranslation()
  const balances = useHandoverBalances(eventId, true)
  const rows = (balances.data ?? []).filter((row) => row.held_minor > 0)

  return (
    <Card className="mt-6">
      <p className="overline text-content-muted">{t('pos.handover.title')}</p>
      <p className="mt-1 text-sm text-content-secondary">{t('pos.handover.hint')}</p>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-content-muted">{t('pos.handover.nobody')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-line-subtle border-t border-line-subtle">
          {rows.map((row) => (
            // La clé porte le solde : après une remise partielle, le montant
            // proposé repart du nouveau solde au lieu de garder l'ancien.
            <HandoverRow
              key={`${row.user_id}:${row.held_minor}`}
              row={row}
              eventId={eventId}
              money={money}
            />
          ))}
        </ul>
      )}
    </Card>
  )
}

function HandoverRow({
  row,
  eventId,
  money,
}: {
  row: WaiterBalance
  eventId: string | undefined
  money: (minor: number) => string
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const receive = useReceiveHandover(eventId)
  const digits = minorUnits(row.currency)
  const [amount, setAmount] = useState((row.held_minor / 10 ** digits).toFixed(digits))
  const value = Number.parseFloat(amount.replace(',', '.'))
  const minor = Number.isFinite(value) && value > 0 ? Math.round(value * 10 ** digits) : null

  return (
    <li className="py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-content-primary">{row.name}</span>
        <span className="shrink-0 font-display text-heading-sm tabular-nums text-champagne-300">
          {money(row.held_minor)}
        </span>
      </div>
      <div className="mt-2 flex gap-2">
        <input
          inputMode="decimal"
          value={amount}
          aria-label={t('pos.handover.amount')}
          onChange={(event) => setAmount(event.target.value)}
          className="h-11 min-w-0 flex-1 rounded-md border border-line bg-bg-base px-3 text-content-primary"
        />
        <Button
          size="md"
          disabled={receive.isPending || minor === null || minor > row.held_minor}
          onClick={() => {
            if (minor !== null) receive.mutate({ waiter_id: row.user_id, amount_minor: minor })
          }}
        >
          {t('pos.handover.receive')}
        </Button>
      </div>
      {receive.isError ? (
        <p className="mt-1 text-sm text-state-danger">{errorText(receive.error)}</p>
      ) : null}
    </li>
  )
}
